import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePause, CirclePlay, OctagonX, Syringe } from 'lucide-react';
import { IPD_HOLD_REASON_CODES, IPD_ORDERS_CHANGED_EVENT, type IpdHoldReasonCode, type IpdOrderKind, type IpdOrderPage, type IpdOrderStopped, type IpdOrderView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, relative, timeOnly } from '@/lib/utils';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { ConfirmDialog, DataTable, type DataTableColumn } from '@/components/ui/layout';
import { Badge, Button, Field, StatusBadge } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { statusLabel } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { admissionIsOpen, type Admission } from '../../admission/types';
import { ExtraDoseDialog } from './ExtraDoseDialog';
import {
  ORDER_LIST_TABS,
  extraDoseDefaults,
  hasAnyAction,
  ipdOrderKeysToRefresh,
  ipdOrdersKey,
  ipdOrdersPath,
  kindsPresent,
  orderActions,
  orderDetailParts,
  orderStateTone,
  stopDescriptionKey,
  stopNotice,
  type OrderListTab,
} from './order-list';

const ALL = 'all';
type KindChoice = IpdOrderKind | typeof ALL;

const EMPTY_KEY: Record<OrderListTab, string> = {
  running: 'ws_no_active_orders',
  held: 'io_ord_empty_held',
  stopped: 'io_ord_empty_stopped',
};

const DEFAULT_HOLD_REASON: IpdHoldReasonCode = 'clinical_hold';

function OrderCell({ order, showKind }: { order: IpdOrderView; showKind: boolean }) {
  const details = orderDetailParts(order).join(' · ');
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {showKind && <Badge variant="secondary">{tr('ipd', `io_kind_${order.kind}`)}</Badge>}
        <span className="font-medium">{order.label}</span>
        {order.priority !== 'routine' && <Badge variant={order.priority === 'stat' ? 'destructive' : 'warning'}>{tr('ipd', `io_priority_${order.priority}`)}</Badge>}
        {order.isHighAlert && <Badge variant="destructive">{tr('ipd', 'io_ord_high_alert')}</Badge>}
      </div>
      {details && <p className="text-xs text-muted-foreground">{details}</p>}
      {order.instructions && <p className="line-clamp-2 text-xs text-muted-foreground">{order.instructions}</p>}
    </div>
  );
}

function WhenCell({ order }: { order: IpdOrderView }) {
  return (
    <div className="text-sm">
      <p className="tabular">{dateTime(order.startsAt)}</p>
      <p className="text-xs text-muted-foreground">{order.endsAt ? tr('ipd', 'io_ord_until_x', { time: dateTime(order.endsAt) }) : tr('ipd', 'io_ord_no_end')}</p>
    </div>
  );
}

function ContextCell({ order }: { order: IpdOrderView }) {
  if (order.state === 'held') {
    return (
      <div className="text-sm">
        <p className="font-medium">{order.holdReasonCode ? tr('ipd', `io_reason_${order.holdReasonCode}`) : tr('ipd', 'io_state_held')}</p>
        {order.holdReason && <p className="line-clamp-2 text-xs text-muted-foreground">{order.holdReason}</p>}
        {order.heldAt && <p className="text-xs text-muted-foreground">{tr('ipd', 'io_ord_held_at_x', { time: dateTime(order.heldAt) })}</p>}
      </div>
    );
  }
  if (order.state === 'stopped' || order.state === 'completed') {
    return (
      <div className="text-sm">
        {order.stopReason ? <p className="line-clamp-2">{order.stopReason}</p> : <p>{tr('ipd', `io_state_${order.state}`)}</p>}
        {order.discontinuedAt && <p className="text-xs text-muted-foreground">{tr('ipd', 'io_ord_stopped_at_x', { time: dateTime(order.discontinuedAt) })}</p>}
      </div>
    );
  }
  if (!order.nextTaskAt) return <span className="text-sm text-muted-foreground">{tr('ipd', 'io_ord_no_next_task')}</span>;
  return (
    <span className="text-sm">
      {relative(order.nextTaskAt)} <span className="tabular text-muted-foreground">· {timeOnly(order.nextTaskAt)}</span>
    </span>
  );
}

const CONTEXT_HEADER: Record<OrderListTab, string> = {
  running: 'io_ord_col_next',
  held: 'io_ord_col_hold',
  stopped: 'io_ord_col_stop',
};

export function OrderList({ admission, kind, filterByKind = false }: { admission: Admission; kind?: IpdOrderKind; filterByKind?: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<OrderListTab>('running');
  const [kindFilter, setKindFilter] = useState<IpdOrderKind | null>(null);
  const [frozenKinds, setFrozenKinds] = useState<IpdOrderKind[] | null>(null);
  const [stopping, setStopping] = useState<IpdOrderView | null>(null);
  const [holding, setHolding] = useState<IpdOrderView | null>(null);
  const [holdCode, setHoldCode] = useState<IpdHoldReasonCode>(DEFAULT_HOLD_REASON);
  const [resuming, setResuming] = useState<IpdOrderView | null>(null);
  const [dosing, setDosing] = useState<IpdOrderView | null>(null);
  const shownKind = kind ?? kindFilter;
  const open = admissionIsOpen(admission);

  useRealtimeInvalidate([`admission:${admission.id}`, `emar:${admission.id}`], [IPD_ORDERS_CHANGED_EVENT, 'cpoe.ordered'], [ipdOrdersKey(admission.id)]);

  const orders = useInfiniteQuery({
    queryKey: ipdOrdersKey(admission.id, tab, shownKind),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => get<IpdOrderPage>(ipdOrdersPath(admission.id, tab, shownKind, pageParam || null), signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = orders.data?.pages.flatMap((page) => page.data) ?? [];

  const refresh = () => {
    for (const queryKey of ipdOrderKeysToRefresh(admission.id)) void queryClient.invalidateQueries({ queryKey });
  };

  const stop = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post<IpdOrderStopped>(`/ipd/orders/${id}/stop`, { reason }),
    onSuccess: (result) => {
      setStopping(null);
      const notice = stopNotice(result, statusLabel);
      toast[notice.tone](notice.title, notice.description);
      refresh();
    },
    onError: (caught) => toast.error(tr('ipd', 'io_ord_could_not_stop'), describeError(caught)),
  });

  const hold = useMutation({
    mutationFn: ({ id, reasonCode, reason }: { id: string; reasonCode: IpdHoldReasonCode; reason: string }) => post<IpdOrderView>(`/ipd/orders/${id}/hold`, { reasonCode, reason }),
    onSuccess: () => {
      setHolding(null);
      toast.success(tr('ipd', 'io_ord_held_toast'));
      refresh();
    },
    onError: (caught) => toast.error(tr('ipd', 'io_ord_could_not_hold'), describeError(caught)),
  });

  const resume = useMutation({
    mutationFn: (id: string) => post<IpdOrderView>(`/ipd/orders/${id}/resume`, {}),
    onSuccess: () => {
      setResuming(null);
      toast.success(tr('ipd', 'io_ord_resumed_toast'));
      refresh();
    },
    onError: (caught) => toast.error(tr('ipd', 'io_ord_could_not_resume'), describeError(caught)),
  });

  const changeTab = (next: OrderListTab) => {
    setTab(next);
    setKindFilter(null);
    setFrozenKinds(null);
  };

  const chooseKind = (choice: KindChoice) => {
    if (choice === ALL) {
      setKindFilter(null);
      setFrozenKinds(null);
      return;
    }
    if (!kindFilter) setFrozenKinds(kindsPresent(rows));
    setKindFilter(choice);
  };

  const chipKinds = kindFilter ? (frozenKinds ?? [kindFilter]) : kindsPresent(rows);
  const showChips = filterByKind && !kind && (kindFilter !== null || chipKinds.length > 1);

  const columns: DataTableColumn<IpdOrderView>[] = [
    { id: 'order', header: tr('ipd', 'io_ord_col_order'), cell: (row) => <OrderCell order={row} showKind={!kind} /> },
    {
      id: 'state',
      header: tr('ipd', 'io_ord_col_state'),
      width: '120px',
      cell: (row) => <StatusBadge status={row.state} tone={orderStateTone(row.state)} label={tr('ipd', `io_state_${row.state}`)} className="normal-case" />,
    },
    { id: 'when', header: tr('ipd', 'io_ord_col_when'), width: '190px', hideBelow: 'md', cell: (row) => <WhenCell order={row} /> },
    { id: 'context', header: tr('ipd', CONTEXT_HEADER[tab]), width: '200px', cell: (row) => <ContextCell order={row} /> },
  ];

  const actionsFor = (row: IpdOrderView) => {
    const actions = orderActions(row, { open, can });
    if (!hasAnyAction(actions)) return null;
    return (
      <div className="flex flex-wrap justify-end gap-1">
        {actions.extraDose && (
          <Button size="sm" variant="outline" onClick={() => setDosing(row)}>
            <Syringe className="h-4 w-4" aria-hidden /> {extraDoseDefaults(row).prn ? tr('ipd', 'io_ord_prn_dose') : tr('ipd', 'io_ord_extra_dose')}
          </Button>
        )}
        {actions.resume && (
          <Button size="sm" variant="outline" onClick={() => setResuming(row)}>
            <CirclePlay className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_ord_resume')}
          </Button>
        )}
        {actions.hold && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setHoldCode(DEFAULT_HOLD_REASON);
              setHolding(row);
            }}
          >
            <CirclePause className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_ord_hold')}
          </Button>
        )}
        {actions.stop && (
          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setStopping(row)}>
            <OctagonX className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_ord_stop')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-96">
          <ChoiceButtons<OrderListTab>
            label={tr('ipd', 'io_ord_tabs_label')}
            value={tab}
            onChange={changeTab}
            options={ORDER_LIST_TABS.map((value) => ({ value, label: tr('ipd', `io_ord_tab_${value}`) }))}
          />
        </div>
      </div>
      {showChips && (
        <ChoiceButtons<KindChoice>
          layout="chips"
          value={kindFilter ?? ALL}
          onChange={chooseKind}
          label={tr('ipd', 'io_ord_filter_label')}
          options={[{ value: ALL, label: tr('ipd', 'io_ord_filter_all') }, ...chipKinds.map((value) => ({ value, label: tr('ipd', `io_kind_${value}`) }))]}
        />
      )}
      <DataTable
        rows={orders.data ? rows : undefined}
        loading={orders.isLoading}
        refreshing={orders.isFetching && !orders.isFetchingNextPage}
        error={orders.error}
        rowKey={(row) => row.id}
        columns={columns}
        rowActions={actionsFor}
        mobileCards
        empty={tr('ipd', EMPTY_KEY[tab])}
        footer={
          orders.hasNextPage ? (
            <div className="flex w-full justify-center">
              <Button size="sm" variant="outline" onClick={() => void orders.fetchNextPage()} loading={orders.isFetchingNextPage}>
                {tr('ipd', 'io_ord_load_more')}
              </Button>
            </div>
          ) : undefined
        }
      />
      {stopping && (
        <ReasonDialog
          title={tr('ipd', 'io_ord_stop_title_x', { label: stopping.label })}
          description={tr('ipd', stopDescriptionKey(stopping.kind))}
          minLength={3}
          maxLength={300}
          submitLabel={tr('ipd', 'io_ord_stop')}
          destructive
          loading={stop.isPending}
          onSubmit={(reason) => stop.mutate({ id: stopping.id, reason })}
          onClose={() => setStopping(null)}
        />
      )}
      {holding && (
        <ReasonDialog
          title={tr('ipd', 'io_ord_hold_title_x', { label: holding.label })}
          description={tr('ipd', 'io_ord_hold_desc')}
          minLength={3}
          maxLength={300}
          submitLabel={tr('ipd', 'io_ord_hold')}
          loading={hold.isPending}
          onSubmit={(reason) => hold.mutate({ id: holding.id, reasonCode: holdCode, reason })}
          onClose={() => setHolding(null)}
        >
          <Field label={tr('ipd', 'io_ord_hold_reason_code')} required>
            <ChoiceButtons<IpdHoldReasonCode>
              layout="chips"
              value={holdCode}
              onChange={setHoldCode}
              label={tr('ipd', 'io_ord_hold_reason_code')}
              options={IPD_HOLD_REASON_CODES.map((value) => ({ value, label: tr('ipd', `io_reason_${value}`) }))}
            />
          </Field>
        </ReasonDialog>
      )}
      {resuming && (
        <ConfirmDialog
          title={tr('ipd', 'io_ord_resume_title_x', { label: resuming.label })}
          description={tr('ipd', 'io_ord_resume_desc')}
          confirmLabel={tr('ipd', 'io_ord_resume')}
          loading={resume.isPending}
          onConfirm={() => resume.mutate(resuming.id)}
          onClose={() => setResuming(null)}
        >
          <p className="text-sm text-muted-foreground">{orderDetailParts(resuming).join(' · ') || resuming.label}</p>
        </ConfirmDialog>
      )}
      {dosing && (
        <ExtraDoseDialog
          order={dosing}
          onClose={() => setDosing(null)}
          onSaved={() => {
            setDosing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
